import { RadialWheel, type WheelSegment } from './radialwheel';

// Hammer menu. A Rust-style RADIAL WHEEL whose segments are the hammer's MODES: Remove, Repair, Pick Up,
// or upgrade to Wood / Stone / Metal / Reinforced. (Twig is the default build material, so there is no
// "upgrade to twig" -- you build twig with the Blueprint and upgrade it with the Hammer.) Picking a mode
// is STICKY: it stays selected and drives what LEFT-CLICK does to the aimed piece. Hold right mouse to
// open, hover a mode, release (or click) to select.
//
// RUST PARITY (owner 2026-07-12): the wheel is CONTEXT-AWARE. Before every open, main.ts calls
// refresh() with what the crosshair is on -- each upgrade slice shows its LIVE COST and greys when you
// can't afford it (or the aimed piece is already that tier or higher), Remove greys when the aimed
// target can't be removed, and Pick Up greys unless a pickupable deployable is aimed.

export type HammerMode = 'remove' | 'repair' | 'pickup' | 'wood' | 'stone' | 'metal' | 'reinforced';

// Order is the OWNER'S (2026-07-12): the upgrade ladder reads clockwise from 12 o'clock
// (wood -> stone -> metal -> reinforced), then the utility actions (pick up, repair, remove).
const SEGMENTS: Array<WheelSegment<HammerMode>> = [
  { key: 'wood', label: 'Upgrade\nWood' },
  { key: 'stone', label: 'Upgrade\nStone' },
  { key: 'metal', label: 'Upgrade\nMetal' },
  { key: 'reinforced', label: 'Upgrade\nReinforced' },
  { key: 'pickup', label: 'Pick Up' }, // hold-LMB pickup as a wheel action too (deployables in your claim, empty)
  { key: 'repair', label: 'Repair' },  // restore a damaged piece to full HP (cooldown after damage; costs materials)
  { key: 'remove', label: 'Remove' },
];

export interface HammerWheelContext {
  // Per-upgrade-tier state: cost line ("100 wood") + whether the slice should commit right now.
  tiers: Partial<Record<'wood' | 'stone' | 'metal' | 'reinforced', { cost: string; enabled: boolean }>>;
  canRemove: boolean;  // aimed target exists and may be removed (auth + window/deploy/piece rules)
  canRepair: boolean;  // aimed target is damaged and repair isn't combat-locked
  canPickup: boolean;  // aimed deployable is pickupable (your claim, empty, not excluded)
}

export class HammerWheel extends RadialWheel<HammerMode, never> {
  constructor(private readonly onSelectMode: (m: HammerMode) => void) {
    super({
      segments: SEGMENTS,
      activeSeg: null, // commits act immediately (Rust) -- there is no sticky mode to keep highlighted
      hubFallbackLabel: 'HAMMER',
      onPointerCommit: () => { this.commitSelection(); this.hide(); },
    });
  }

  // Refresh cost lines + availability from the aim context, right before show() (and safe live).
  refresh(ctx: HammerWheelContext): void {
    for (const t of ['wood', 'stone', 'metal', 'reinforced'] as const) {
      const st = ctx.tiers[t];
      this.setSegState(t, { sub: st?.cost ?? '', disabled: !st?.enabled });
    }
    this.setSegState('remove', { disabled: !ctx.canRemove });
    this.setSegState('repair', { disabled: !ctx.canRepair });
    this.setSegState('pickup', { disabled: !ctx.canPickup });
  }

  // Apply the hovered mode (sticky: it remains the active mode). Safe to call with nothing hovered.
  commitSelection(): void {
    this.commit({ onSeg: (m) => this.onSelectMode(m) });
  }
}
